import { Controller } from "@hotwired/stimulus"
import { calculate } from "../modules/salary_lab/calculator_api"
import { formatCurrency, parseNumber } from "../modules/salary_lab/formatter"
import { SmartDebounce, detectEventType } from "../modules/salary_lab/smart_debounce"
import { writeGenericToUrl, readGenericFromUrl } from "../modules/salary_lab/url_state"
import { trackCalculatorUsed } from "../modules/salary_lab/ga4_tracker"

const URL_MAP = { a_type: "at", a_salary: "as", a_rate: "ar", a_rate_type: "art", a_working_weeks: "aww", a_hours_per_week: "ahrs", a_expense_percent: "aexp", a_ks: "aks", a_sl: "asl", b_type: "bt", b_salary: "bs", b_rate: "br", b_rate_type: "brt", b_working_weeks: "bww", b_hours_per_week: "bhrs", b_expense_percent: "bexp", b_ks: "bks", b_sl: "bsl" }
const URL_TYPES = { a_salary: "number", a_rate: "number", a_working_weeks: "number", a_hours_per_week: "number", a_expense_percent: "number", a_ks: "number", a_sl: "boolean", b_salary: "number", b_rate: "number", b_working_weeks: "number", b_hours_per_week: "number", b_expense_percent: "number", b_ks: "number", b_sl: "boolean" }

export default class extends Controller {
  // Values are wired from the view using server-side constants —
  // see Custom::SalaryLab::CalculatorsController#set_salary_lab_constants. No hardcoded
  // defaults here so drift is impossible: if a view forgets to wire
  // a value, the form will visibly break at 0 rather than silently
  // using a stale fallback.
  static values = {
    defaultKiwisaverRate: Number,
    contractorDefaultWeeks: Number,
    contractorDefaultHours: Number,
    contractorDefaultExpensePercent: Number
  }

  static targets = [
    // Scenario A inputs
    "aTypeSelect", "aSalaryInput", "aRateInput", "aRateTypeSelect",
    "aKiwisaverSelect", "aStudentLoanCheckbox",
    "aPayeFields", "aContractorFields",
    "aWorkingWeeksInput", "aHoursInput", "aExpenseInput",
    // Scenario B inputs
    "bTypeSelect", "bSalaryInput", "bRateInput", "bRateTypeSelect",
    "bKiwisaverSelect", "bStudentLoanCheckbox",
    "bPayeFields", "bContractorFields",
    "bWorkingWeeksInput", "bHoursInput", "bExpenseInput",
    // Results
    "resultsPanel", "loadingIndicator",
    "expensesRow", "expensesLabel", "taxableRow",
    "aGross", "aExpenses", "aTaxable", "aNet", "aTax", "aAcc", "aKiwisaver", "aStudentLoan", "aLabel",
    "bGross", "bExpenses", "bTaxable", "bNet", "bTax", "bAcc", "bKiwisaver", "bStudentLoan", "bLabel",
    "studentLoanRow",
    "diffNet", "diffMonthly", "diffFortnightly", "diffPercent",
    "summaryText", "summaryPanel",
    "aMonthlyNet", "aFortnightlyNet", "aWeeklyNet",
    "bMonthlyNet", "bFortnightlyNet", "bWeeklyNet",
    "liveRegion"
  ]

  connect() {
    this._debounce = new SmartDebounce(
      () => this._performCalculation(),
      { delays: { typing: 400, deletion: 800, toggle: 200 }, minDigits: 4 }
    )
    this._lastParams = null
    this._showTypeFields("a")
    this._showTypeFields("b")
    this._prefillFromUrl()
    this._renderPrerenderedExtras()
    this._storeCurrentParams(this._collectParams())
  }

  disconnect() {
    this._debounce.cancel()
  }

  typeChanged(event) {
    const scenario = event.currentTarget.dataset.scenario
    this._showTypeFields(scenario)
    this._debounce.schedule("toggle")
  }

  inputChanged(event) {
    const value = event?.target?.value
    this._debounce.schedule(detectEventType(event), value)
  }

  optionChanged() {
    this._debounce.schedule("toggle")
  }

  applyPreset(event) {
    event.preventDefault()
    const preset = event.currentTarget.dataset.preset
    this._applyPreset(preset)
    this._debounce.schedule("submit")
  }

  async submitForm(event) {
    event.preventDefault()
    this._debounce.schedule("submit")
  }

  // --- Private ---

  _showTypeFields(scenario) {
    const prefix = scenario
    const typeTarget = `${prefix}TypeSelect`
    const capitalized = typeTarget.charAt(0).toUpperCase() + typeTarget.slice(1)
    if (!this[`has${capitalized}Target`]) return

    const type = this[`${typeTarget}Target`].value
    const payeTarget = `${prefix}PayeFields`
    const contractorTarget = `${prefix}ContractorFields`
    const payeCap = payeTarget.charAt(0).toUpperCase() + payeTarget.slice(1)
    const contractorCap = contractorTarget.charAt(0).toUpperCase() + contractorTarget.slice(1)

    if (this[`has${payeCap}Target`]) {
      this[`${payeTarget}Target`].classList.toggle("hidden", type === "contractor")
    }
    if (this[`has${contractorCap}Target`]) {
      this[`${contractorTarget}Target`].classList.toggle("hidden", type !== "contractor")
    }
  }

  async _performCalculation() {
    const params = this._collectParams()
    if (!this._hasInput(params)) return

    const paramsKey = JSON.stringify(params)
    if (paramsKey === this._lastParams) return
    this._lastParams = paramsKey

    this._showLoading(true)

    try {
      const result = await calculate(params)
      this._renderResult(result)
      this._writeUrlState(params)
      this._storeCurrentParams(params)
      trackCalculatorUsed("comparison")
    } catch (error) {
      if (this.hasLiveRegionTarget) {
        this.liveRegionTarget.textContent = error.message
      }
    } finally {
      this._showLoading(false)
    }
  }

  _hasInput(params) {
    const a = params.scenario_a
    const b = params.scenario_b
    const aVal = a.type === "contractor" ? a.rate : a.gross_salary
    const bVal = b.type === "contractor" ? b.rate : b.gross_salary
    return (aVal && aVal > 0) || (bVal && bVal > 0)
  }

  _collectParams() {
    return {
      type: "comparison",
      scenario_a: this._collectScenario("a"),
      scenario_b: this._collectScenario("b"),
    }
  }

  _collectScenario(prefix) {
    const typeTarget = `${prefix}TypeSelect`
    const typeCap = typeTarget.charAt(0).toUpperCase() + typeTarget.slice(1)
    const type = this[`has${typeCap}Target`] ? this[`${typeTarget}Target`].value : "paye"

    if (type === "contractor") {
      return {
        type: "contractor",
        rate: this._getNumber(`${prefix}RateInput`),
        rate_type: this._getValue(`${prefix}RateTypeSelect`, "hourly"),
        working_weeks: this._getNumber(`${prefix}WorkingWeeksInput`, this.contractorDefaultWeeksValue),
        hours_per_week: this._getNumber(`${prefix}HoursInput`, this.contractorDefaultHoursValue),
        expense_percent: this._getNumber(`${prefix}ExpenseInput`, this.contractorDefaultExpensePercentValue),
        kiwisaver_perc: this._getNumber(`${prefix}KiwisaverSelect`, 0),
        include_student_loan: this._getChecked(`${prefix}StudentLoanCheckbox`),
      }
    }

    return {
      gross_salary: this._getNumber(`${prefix}SalaryInput`),
      kiwisaver_perc: this._getNumber(`${prefix}KiwisaverSelect`, 0),
      include_student_loan: this._getChecked(`${prefix}StudentLoanCheckbox`),
    }
  }

  _getNumber(targetName, fallback = 0) {
    const cap = targetName.charAt(0).toUpperCase() + targetName.slice(1)
    if (!this[`has${cap}Target`]) return fallback
    const val = parseNumber(this[`${targetName}Target`].value)
    return Number.isFinite(val) ? val : fallback
  }

  _getValue(targetName, fallback = "") {
    const cap = targetName.charAt(0).toUpperCase() + targetName.slice(1)
    if (!this[`has${cap}Target`]) return fallback
    return this[`${targetName}Target`].value || fallback
  }

  _getChecked(targetName) {
    const cap = targetName.charAt(0).toUpperCase() + targetName.slice(1)
    return this[`has${cap}Target`] ? this[`${targetName}Target`].checked : false
  }

  _showLoading(show) {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.toggle("hidden", !show)
    }
  }

  _renderResult(result) {
    if (this.hasResultsPanelTarget) {
      this.resultsPanelTarget.classList.remove("hidden")
    }

    const a = result.scenario_a
    const b = result.scenario_b
    const diff = result.differences

    // Scenario labels
    this._setText("aLabel", a.label)
    this._setText("bLabel", b.label)

    // Scenario A
    this._setText("aGross", formatCurrency(a.gross))
    this._setText("aNet", formatCurrency(a.net))
    this._setText("aTax", formatCurrency(a.tax))
    this._setText("aAcc", formatCurrency(a.acc))
    this._setText("aKiwisaver", formatCurrency(a.kiwisaver))

    // Scenario B
    this._setText("bGross", formatCurrency(b.gross))
    this._setText("bNet", formatCurrency(b.net))
    this._setText("bTax", formatCurrency(b.tax))
    this._setText("bAcc", formatCurrency(b.acc))
    this._setText("bKiwisaver", formatCurrency(b.kiwisaver))

    // Contractor expense/taxable rows — show when either scenario is a contractor
    this._renderContractorRows(a, b)

    // Student loan row — show when either scenario has a student loan
    this._renderStudentLoanRow(a, b)

    // Frequency rows
    if (a.frequencies) {
      this._setText("aMonthlyNet", formatCurrency(a.frequencies.monthly?.net ?? a.net / 12))
      this._setText("aFortnightlyNet", formatCurrency(a.frequencies.fortnightly?.net ?? a.net / 26))
      this._setText("aWeeklyNet", formatCurrency(a.frequencies.weekly?.net ?? a.net / 52))
    }
    if (b.frequencies) {
      this._setText("bMonthlyNet", formatCurrency(b.frequencies.monthly?.net ?? b.net / 12))
      this._setText("bFortnightlyNet", formatCurrency(b.frequencies.fortnightly?.net ?? b.net / 26))
      this._setText("bWeeklyNet", formatCurrency(b.frequencies.weekly?.net ?? b.net / 52))
    }

    // Differences
    const netSign = diff.net_annual >= 0 ? "+" : ""
    this._setText("diffNet", `${netSign}${formatCurrency(diff.net_annual)}/year`)
    this._setText("diffMonthly", `${netSign}${formatCurrency(diff.net_monthly)}/month`)
    this._setText("diffFortnightly", `${netSign}${formatCurrency(diff.net_fortnightly)}/fortnight`)
    this._setText("diffPercent", `${netSign}${diff.percentage_change}%`)

    // Summary
    this._setText("summaryText", result.summary)
    if (this.hasSummaryPanelTarget) {
      const isPositive = diff.winner === "b"
      const isNegative = diff.winner === "a"
      this.summaryPanelTarget.className = this.summaryPanelTarget.className
        .replace(/bg-\w+-50/g, "")
        .replace(/border-\w+-200/g, "")
      if (isPositive) {
        this.summaryPanelTarget.classList.add("bg-green-50", "border-green-200")
      } else if (isNegative) {
        this.summaryPanelTarget.classList.add("bg-red-50", "border-red-200")
      } else {
        this.summaryPanelTarget.classList.add("bg-gray-50", "border-gray-200")
      }
    }

    // Color the difference values
    this._colorDiff("diffNet", diff.net_annual)
    this._colorDiff("diffMonthly", diff.net_monthly)
    this._colorDiff("diffFortnightly", diff.net_fortnightly)
    this._colorDiff("diffPercent", diff.net_annual)

    this.dispatch("calculated", { detail: result })
    this._announceResult(result)
  }

  _renderStudentLoanRow(a, b) {
    const aLoan = a.student_loan || 0
    const bLoan = b.student_loan || 0
    const hasLoan = aLoan > 0 || bLoan > 0

    if (this.hasStudentLoanRowTarget) {
      this.studentLoanRowTarget.classList.toggle("hidden", !hasLoan)
    }

    if (hasLoan) {
      this._setText("aStudentLoan", aLoan > 0 ? formatCurrency(aLoan) : "\u2014")
      this._setText("bStudentLoan", bLoan > 0 ? formatCurrency(bLoan) : "\u2014")
    }
  }

  _renderContractorRows(a, b) {
    const hasContractor = a.type === "contractor" || b.type === "contractor"

    if (this.hasExpensesRowTarget) {
      this.expensesRowTarget.classList.toggle("hidden", !hasContractor)
    }
    if (this.hasTaxableRowTarget) {
      this.taxableRowTarget.classList.toggle("hidden", !hasContractor)
    }

    if (!hasContractor) return

    // Expenses row
    if (a.type === "contractor") {
      this._setText("aExpenses", `-${formatCurrency(a.expenses)}`)
    } else {
      this._setText("aExpenses", "\u2014")
    }
    if (b.type === "contractor") {
      this._setText("bExpenses", `-${formatCurrency(b.expenses)}`)
    } else {
      this._setText("bExpenses", "\u2014")
    }

    // Taxable income row
    this._setText("aTaxable", formatCurrency(a.taxable_income ?? a.gross))
    this._setText("bTaxable", formatCurrency(b.taxable_income ?? b.gross))
  }

  _colorDiff(targetName, value) {
    const cap = targetName.charAt(0).toUpperCase() + targetName.slice(1)
    if (!this[`has${cap}Target`]) return
    const el = this[`${targetName}Target`]
    el.classList.remove("text-green-700", "text-red-700", "text-gray-700")
    if (value > 0) el.classList.add("text-green-700")
    else if (value < 0) el.classList.add("text-red-700")
    else el.classList.add("text-gray-700")
  }

  _setText(targetName, text) {
    const cap = targetName.charAt(0).toUpperCase() + targetName.slice(1)
    if (this[`has${cap}Target`]) {
      this[`${targetName}Target`].textContent = text
    }
  }

  _applyPreset(preset) {
    switch (preset) {
      case "pay-rise":
        this._setScenarioType("a", "paye")
        this._setScenarioType("b", "paye")
        this._setInput("aSalaryInput", "80000")
        this._setInput("bSalaryInput", "90000")
        break
      case "paye-vs-contractor":
        this._setScenarioType("a", "paye")
        this._setScenarioType("b", "contractor")
        this._setInput("aSalaryInput", "85000")
        this._setInput("bRateInput", "55")
        break
      case "kiwisaver":
        this._setScenarioType("a", "paye")
        this._setScenarioType("b", "paye")
        this._setInput("aSalaryInput", "80000")
        this._setInput("bSalaryInput", "80000")
        this._setSelect("aKiwisaverSelect", String(this.defaultKiwisaverRateValue))
        this._setSelect("bKiwisaverSelect", "6")
        break
    }
    this._showTypeFields("a")
    this._showTypeFields("b")
  }

  _setScenarioType(prefix, type) {
    const target = `${prefix}TypeSelect`
    const cap = target.charAt(0).toUpperCase() + target.slice(1)
    if (this[`has${cap}Target`]) {
      this[`${target}Target`].value = type
    }
  }

  _setInput(targetName, value) {
    const cap = targetName.charAt(0).toUpperCase() + targetName.slice(1)
    if (this[`has${cap}Target`]) {
      this[`${targetName}Target`].value = value
    }
  }

  _setSelect(targetName, value) {
    const cap = targetName.charAt(0).toUpperCase() + targetName.slice(1)
    if (this[`has${cap}Target`]) {
      this[`${targetName}Target`].value = value
    }
  }

  _prefillFromUrl() {
    const p = readGenericFromUrl(URL_MAP, URL_TYPES)
    if (!p.a_salary && !p.a_rate && !p.b_salary && !p.b_rate) return
    if (p.a_type) this._setScenarioType("a", p.a_type)
    if (p.a_salary) this._setInput("aSalaryInput", String(p.a_salary))
    if (p.a_rate) this._setInput("aRateInput", String(p.a_rate))
    if (p.a_rate_type) this._setSelect("aRateTypeSelect", p.a_rate_type)
    if (p.a_working_weeks) this._setInput("aWorkingWeeksInput", String(p.a_working_weeks))
    if (p.a_hours_per_week) this._setInput("aHoursInput", String(p.a_hours_per_week))
    if (p.a_expense_percent) this._setInput("aExpenseInput", String(p.a_expense_percent))
    if (p.a_ks != null) this._setSelect("aKiwisaverSelect", String(p.a_ks))
    if (p.a_sl) { const c = "aStudentLoanCheckbox"; const cap = c.charAt(0).toUpperCase() + c.slice(1); if (this[`has${cap}Target`]) this[`${c}Target`].checked = true }
    if (p.b_type) this._setScenarioType("b", p.b_type)
    if (p.b_salary) this._setInput("bSalaryInput", String(p.b_salary))
    if (p.b_rate) this._setInput("bRateInput", String(p.b_rate))
    if (p.b_rate_type) this._setSelect("bRateTypeSelect", p.b_rate_type)
    if (p.b_working_weeks) this._setInput("bWorkingWeeksInput", String(p.b_working_weeks))
    if (p.b_hours_per_week) this._setInput("bHoursInput", String(p.b_hours_per_week))
    if (p.b_expense_percent) this._setInput("bExpenseInput", String(p.b_expense_percent))
    if (p.b_ks != null) this._setSelect("bKiwisaverSelect", String(p.b_ks))
    if (p.b_sl) { const c = "bStudentLoanCheckbox"; const cap = c.charAt(0).toUpperCase() + c.slice(1); if (this[`has${cap}Target`]) this[`${c}Target`].checked = true }
    this._showTypeFields("a"); this._showTypeFields("b")
    if (this.element.dataset.prerendered !== "true") this._performCalculation()
  }

  _writeUrlState(params) {
    const flat = {}
    const a = params.scenario_a
    const b = params.scenario_b
    flat.a_type = a.type || "paye"
    if (a.gross_salary) flat.a_salary = a.gross_salary
    if (a.rate) flat.a_rate = a.rate
    if (a.rate_type) flat.a_rate_type = a.rate_type
    if (a.working_weeks) flat.a_working_weeks = a.working_weeks
    if (a.hours_per_week) flat.a_hours_per_week = a.hours_per_week
    if (a.expense_percent) flat.a_expense_percent = a.expense_percent
    if (a.kiwisaver_perc) flat.a_ks = a.kiwisaver_perc
    if (a.include_student_loan) flat.a_sl = a.include_student_loan
    flat.b_type = b.type || "paye"
    if (b.gross_salary) flat.b_salary = b.gross_salary
    if (b.rate) flat.b_rate = b.rate
    if (b.rate_type) flat.b_rate_type = b.rate_type
    if (b.working_weeks) flat.b_working_weeks = b.working_weeks
    if (b.hours_per_week) flat.b_hours_per_week = b.hours_per_week
    if (b.expense_percent) flat.b_expense_percent = b.expense_percent
    if (b.kiwisaver_perc) flat.b_ks = b.kiwisaver_perc
    if (b.include_student_loan) flat.b_sl = b.include_student_loan
    writeGenericToUrl(flat, URL_MAP)
  }

  _storeCurrentParams(params) {
    this.element.dataset.currentParams = JSON.stringify(params)
  }

  _renderPrerenderedExtras() {
    if (this.element.dataset.prerendered !== "true") return
    const json = this.element.dataset.prerenderedResults
    if (!json) return
    try {
      const data = JSON.parse(json)
      this._renderResult(data)
    } catch { /* ignore parse errors */ }
  }

  _announceResult(result) {
    if (!this.hasLiveRegionTarget) return
    this.liveRegionTarget.textContent = result.summary
  }
}
